// GameConfigPanel — Phase 12.8 panel inside GamesHub.
// Was AdminGameConfig.tsx (per-game tunables).
//
// Mounted at /games/:gameId. Looks the game up in the frontend
// registry — title, icon, theme, and the config API target all come
// from the GameModule, so adding a new game requires no edits here.
//
// Phase 5 UI overhaul (2026-05-15): heading + subtitle re-keyed to
// arena/neon tokens. The actual config form is rendered by the
// shared-ui <ConfigPanel /> component which has its own styling.

import { useParams, Navigate } from 'react-router-dom';
import { NEUTRAL_ADMIN_THEME } from '@baaligames/shared-ui/admin/components/AdminLayout';
import ConfigPanel from '@baaligames/shared-ui/admin/components/ConfigPanel';
import { getGame } from '@baaligames/shared-ui/games/registry';
import CricketAdminPanel from './cricket/CricketAdminPanel';
import MatkaPanel from '../matka/MatkaPanel';

// GM-4: Football & Tennis are their own admin game tabs but share cricket's
// proven ops panel — routed here as sport-scoped views (not in the shared-ui
// registry, so the agent end is untouched). Cricket is the registry entry.
export default function GameConfigPanel() {
  const { gameId } = useParams<{ gameId: string }>();

  // Football / Tennis: reuse the cricket ops panel, scoped to the sport.
  if (gameId === 'football' || gameId === 'tennis') {
    return <CricketAdminPanel theme={NEUTRAL_ADMIN_THEME} initialSport={gameId} />;
  }

  const g = gameId ? getGame(gameId) : undefined;

  // Unknown game id → kick back to the games hub (now under Settings).
  if (!g) return <Navigate to="/settings/games" replace />;

  if (g.id === 'cricket') {
    return <CricketAdminPanel theme={g.adminTheme || NEUTRAL_ADMIN_THEME} initialSport="cricket" />;
  }

  // Matka King (2026-09-19): same precedent as cricket — the game's page under
  // Settings → Games IS its control room (games, results, targets, hisaab…),
  // with the generic money form living on the room's own "Settings" tab. The
  // owner looked for "where do I add / edit games" here and found only the
  // tunables form, so the two are one page now. /matka is the same panel.
  if (g.id === 'matka_king') {
    return <MatkaPanel />;
  }

  const theme = g.adminTheme || NEUTRAL_ADMIN_THEME;

  // 2026-09-06 (Phase D): a game can be in the registry for its NAME and TILE
  // while having no backend module — gems_mine is the only one today. Asking
  // ConfigPanel to load `/admin/config/gems_mine` would surface a bare "unknown
  // game" error, which reads like a platform fault rather than the truth, so we
  // say the truth instead. The flag lives on the registry entry, not on an id
  // check here, so the day the module lands nothing in this file changes.
  if (g.hasAdminConfig === false) {
    return (
      <div className="space-y-4">
        <h2 className="text-[15px] font-bold text-arena-900 dark:text-white">
          {g.displayName} controls
        </h2>
        <div className="rounded-xl border border-amber-300 dark:border-amber-700/70 bg-amber-50 dark:bg-amber-950/30 px-4 py-3">
          <div className="text-[13.5px] font-bold text-amber-900 dark:text-amber-200">
            No settings yet — this game has no money path.
          </div>
          <p className="mt-1 text-[12.5px] text-amber-800/90 dark:text-amber-200/75">
            {g.displayName} is still a practice-chips build: it runs entirely in the
            player&rsquo;s browser, no real rupees move, and there is nothing to tune. Its
            tunables appear here automatically once its backend module ships. Until then
            you can still show or hide it in{' '}
            <b>Master control &rarr; Show / Hide &amp; Lock every game</b>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-[15px] font-bold text-arena-900 dark:text-white">
        {g.displayName} controls
      </h2>
      <p className="text-[12.5px] text-arena-600 dark:text-neon-200/70">
        All tunables are applied live to the next match — no server restart needed.
      </p>
      <ConfigPanel game={g.id} theme={theme} />
    </div>
  );
}
